/**
 * Shared primitives for oracle/instructions/* builders.
 *
 * Phase 6 slice 4: migrated from web3.js `PublicKey` + Anchor `BN` to Kit
 * `Address` + `bigint`. PDA derivation now goes through the Codama-generated
 * helpers in `@betmebro/clients-js/generated` where available
 * (`findBetPda`, `findConfigPda`, `findEscrowPda`); the few seed-shape
 * details Codama can't derive (Market PDA seeded by an instruction-level
 * arg, OracleConfig PDA in the sibling program) are hand-written here using
 * Kit's `getProgramDerivedAddress` + encoders.
 *
 * Seeds match contracts/betmebro/src/constants.rs:
 *   MARKET_SEED  = "market"
 *   ESCROW_SEED  = "escrow"
 *   BET_SEED     = "bet"
 *   CONFIG_SEED  = "config"
 *
 * Detrust-oracle seeds match `detrust_oracle::constants::REQUEST_SEED` in
 * the sibling repo at the rev pinned in workers/clients-js (currently
 * 9a4e5b4...).
 */

import {
  address as toAddress,
  getAddressEncoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getU8Encoder,
  getU64Encoder,
} from "@solana/kit";
import type { Address } from "@solana/kit";

import {
  BETMEBRO_PROGRAM_ADDRESS,
  findBetPda,
  findConfigPda,
  findEscrowPda,
} from "@betmebro/clients-js/generated";

export const BETMEBRO_PROGRAM_ID: Address = BETMEBRO_PROGRAM_ADDRESS;

/**
 * detrust-oracle program ID. Sourced from CLAUDE.md "detrust-oracle 依赖" +
 * the sibling repo's `declare_id!`. v1 uses devnet deploy ID (filled at Day
 * 6 cutover); the constant here pins the canonical address used for PDA
 * derivation, NOT the wrangler env's runtime ORACLE_PROGRAM_ID (those should
 * match post-deploy).
 */
export const DETRUST_ORACLE_PROGRAM_ID: Address = toAddress(
  "2Z39QcznGX4RV9nZcepZPc1HRwAswLuRiY3PVqzmg3Bi",
);

const MARKET_SEED = new Uint8Array([109, 97, 114, 107, 101, 116]); // "market"
const CONFIG_SEED = new Uint8Array([99, 111, 110, 102, 105, 103]); // "config"
const ORACLE_REQUEST_SEED = new Uint8Array([114, 101, 113, 117, 101, 115, 116]); // "request"
// admin-resolve-deprecation §4.1 (2026-05-18): per-market oracle requester PDA.
// CPI Create with PDA pattern — letting close_resolved_market invoke_signed
// for `consume` without needing creator-keypair availability.
const ORACLE_REQ_SEED = new Uint8Array([
  111, 114, 97, 99, 108, 101, 95, 114, 101, 113,
]); // "oracle_req"

/** Derive Market PDA from u64 market_id (LE-encoded). */
export async function deriveMarketPda(marketId: bigint): Promise<Address> {
  const [pda] = await getProgramDerivedAddress({
    programAddress: BETMEBRO_PROGRAM_ID,
    seeds: [
      getBytesEncoder().encode(MARKET_SEED),
      getU64Encoder().encode(marketId),
    ],
  });
  return pda;
}

/** Derive Escrow PDA from a Market PDA address (Codama-generated). */
export async function deriveEscrowPda(market: Address): Promise<Address> {
  const [pda] = await findEscrowPda({ market });
  return pda;
}

/** Derive Bet PDA from a Market PDA + user address (Codama-generated). */
export async function deriveBetPda(
  market: Address,
  user: Address,
): Promise<Address> {
  const [pda] = await findBetPda({ market, user });
  return pda;
}

/** Derive betmebro GlobalConfig PDA (singleton, seeds = [b"config"]). */
export async function deriveConfigPda(): Promise<Address> {
  const [pda] = await findConfigPda();
  return pda;
}

/** Derive detrust-oracle's OracleConfig PDA (singleton, seeds = [b"config"]). */
export async function deriveOracleConfigPda(): Promise<Address> {
  const [pda] = await getProgramDerivedAddress({
    programAddress: DETRUST_ORACLE_PROGRAM_ID,
    seeds: [getBytesEncoder().encode(CONFIG_SEED)],
  });
  return pda;
}

/**
 * Derive the per-market oracle requester PDA (admin-resolve-deprecation §4.1).
 *
 * Seeds match `betmebro::constants::ORACLE_REQ_SEED + market_id.to_le_bytes()`.
 * This PDA becomes the oracle's `DataRequest.requester` (NOT the user wallet),
 * enabling `close_resolved_market` to invoke_signed for `consume`.
 */
export async function deriveOracleReqPda(marketId: bigint): Promise<Address> {
  const [pda] = await getProgramDerivedAddress({
    programAddress: BETMEBRO_PROGRAM_ID,
    seeds: [
      getBytesEncoder().encode(ORACLE_REQ_SEED),
      getU64Encoder().encode(marketId),
    ],
  });
  return pda;
}

/**
 * Derive detrust-oracle's DataRequest PDA for a given requester + nonce.
 * Seeds match `detrust_oracle::request::request_data` accounts macro:
 *   [REQUEST_SEED, requester (= betmebro oracle_req_pda post §4.1), nonce (=market_id LE)]
 *
 * Note (admin-resolve-deprecation §4.1, 2026-05-18): the `requester` is now
 * the betmebro per-market oracle_req_pda (see `deriveOracleReqPda` above),
 * NOT the user wallet that signed `create_market`.
 */
export async function deriveOracleDataRequestPda(
  requester: Address,
  marketId: bigint,
): Promise<Address> {
  const [pda] = await getProgramDerivedAddress({
    programAddress: DETRUST_ORACLE_PROGRAM_ID,
    seeds: [
      getBytesEncoder().encode(ORACLE_REQUEST_SEED),
      getAddressEncoder().encode(requester),
      getU64Encoder().encode(marketId),
    ],
  });
  return pda;
}

/**
 * oracle Escrow PDA — seeds [b"escrow", data_request] under the oracle
 * program. The generated `findEscrowPda` lives in a /pdas subpath the
 * clients-js exports map doesn't expose, so derive locally (same pattern as
 * the other oracle PDAs above).
 */
export async function deriveOracleEscrowPda(
  dataRequest: Address,
): Promise<Address> {
  const [pda] = await getProgramDerivedAddress({
    programAddress: DETRUST_ORACLE_PROGRAM_ID,
    seeds: [
      getBytesEncoder().encode(new Uint8Array([101, 115, 99, 114, 111, 119])), // "escrow"
      getAddressEncoder().encode(dataRequest),
    ],
  });
  return pda;
}

/**
 * oracle Proposal PDA — seeds [b"proposal", data_request, round_u8].
 */
export async function deriveOracleProposalPda(
  dataRequest: Address,
  round: number,
): Promise<Address> {
  const [pda] = await getProgramDerivedAddress({
    programAddress: DETRUST_ORACLE_PROGRAM_ID,
    seeds: [
      getBytesEncoder().encode(
        new Uint8Array([112, 114, 111, 112, 111, 115, 97, 108]),
      ), // "proposal"
      getAddressEncoder().encode(dataRequest),
      getU8Encoder().encode(round),
    ],
  });
  return pda;
}
